import { useState } from "react";
import { CameraIcon, DownloadIcon, MapPinnedIcon } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useEnviarATerreno, useGuiaDeTerreno, useRetirarDeTerreno } from "@/hooks/useInformes";
import { getApiErrorMessage } from "@/lib/api";
import { formatearMomento } from "@/lib/fechas";
import {
  descargarGuiaDeTerrenoRequest,
  type EstadoDeSeccionDeLaGuia,
  type GuiaDeTerreno,
  type TerrenoDelInforme,
} from "@/lib/informes";

/**
 * Rótulo y color del estado de cada sección. El servidor manda un valor de
 * máquina y la pantalla elige cómo decirlo, igual que el PDF.
 */
const ESTADO_DE_SECCION: Record<
  EstadoDeSeccionDeLaGuia,
  { variante: "success" | "warning" | "destructive" | "outline"; rotulo: (n: number) => string }
> = {
  con_datos: { variante: "success", rotulo: (n) => `${n} ${n === 1 ? "registro" : "registros"}` },
  sin_datos: { variante: "destructive", rotulo: () => "Sin datos en la web" },
  no_aplica: { variante: "outline", rotulo: () => "No aplica" },
  por_solicitar: { variante: "warning", rotulo: (n) => `${n} por solicitar` },
  completo: { variante: "success", rotulo: () => "Completo" },
};

interface BotonGuiaDeTerrenoProps {
  informeId: number;
  terreno: TerrenoDelInforme;
}

/**
 * La puerta del informe a la app de terreno (prototipo 23-09-2026, pantalla 95).
 *
 * Un informe asignado NO aparece en la app del encargado hasta que se envía
 * desde acá. El botón dice en qué punto está —"por sincronizar" o
 * "sincronizada"— y abre la guía de validación, que se puede leer, bajar en
 * PDF y, al final, enviar al encargado: eso lo habilita en la app y le manda
 * la guía por correo.
 *
 * Hoy cualquier usuario la ve y la envía. Si vuelve una regla de persona, viaja
 * resuelta en `terreno.puede_gestionar` y no se reimplementa acá.
 */
export function BotonGuiaDeTerreno({ informeId, terreno }: BotonGuiaDeTerrenoProps) {
  const [abierta, setAbierta] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant={terreno.disponible ? "soft" : "outline"}
        onClick={() => setAbierta(true)}
      >
        <MapPinnedIcon className="size-4" />
        {terreno.disponible ? "Guía de terreno · sincronizada" : "Guía de terreno · por sincronizar"}
      </Button>

      <ModalDeGuiaDeTerreno
        informeId={informeId}
        terreno={terreno}
        abierta={abierta}
        onCerrar={() => setAbierta(false)}
      />
    </>
  );
}

function ModalDeGuiaDeTerreno({
  informeId,
  terreno,
  abierta,
  onCerrar,
}: BotonGuiaDeTerrenoProps & { abierta: boolean; onCerrar: () => void }) {
  const guia = useGuiaDeTerreno(informeId, abierta);
  const enviar = useEnviarATerreno(informeId);
  const retirar = useRetirarDeTerreno(informeId);
  const [descargando, setDescargando] = useState(false);

  // Pausada no es en vuelo: sin red TanStack la deja `isPending` sin enviar
  // nada, y bloquear el cierre por eso encerraría a la persona en la modal.
  const enVuelo =
    (enviar.isPending && !enviar.isPaused) || (retirar.isPending && !retirar.isPaused);

  const descargar = async () => {
    setDescargando(true);
    try {
      await descargarGuiaDeTerrenoRequest(informeId);
    } catch {
      toast.error("No pudimos generar la guía en PDF. Inténtalo de nuevo en unos minutos.");
    } finally {
      setDescargando(false);
    }
  };

  const enviarAlEncargado = () => {
    enviar.mutate(undefined, {
      onSuccess: (respuesta) => {
        // El informe quedó habilitado igual: lo que falló fue el aviso, y
        // reenviar desde esta misma modal es un clic.
        if (respuesta.correo_enviado) {
          toast.success("Guía enviada. El informe ya está disponible en la app de terreno.");
        } else {
          toast.warning(
            "El informe ya está disponible en la app, pero el correo no salió. Reenvía la guía en unos minutos.",
          );
        }
      },
      onError: (error) =>
        toast.error(getApiErrorMessage(error, "No pudimos enviar la guía. Inténtalo de nuevo.")),
    });
  };

  const retirarDeLaApp = () => {
    retirar.mutate(undefined, {
      onSuccess: () => toast.success("El informe ya no está disponible en la app de terreno."),
      onError: (error) =>
        toast.error(getApiErrorMessage(error, "No pudimos retirarlo de la app. Inténtalo de nuevo.")),
    });
  };

  const bloqueado = terreno.motivo_no_habilitable ?? null;

  return (
    <Dialog
      open={abierta}
      onOpenChange={(estaAbierta) => {
        if (!estaAbierta && !enVuelo) onCerrar();
      }}
    >
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[60rem]">
        <DialogHeader>
          <DialogTitle>Guía de validación en terreno</DialogTitle>
          <DialogDescription>
            {guia.data ? <Encabezado guia={guia.data} /> : "Lo que hay que validar y preguntar en la visita."}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6">
          {guia.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : guia.isError ? (
            <p className="text-destructive py-6 text-sm">
              No pudimos cargar la guía. Cierra la ventana y vuelve a abrirla.
            </p>
          ) : (
            <ContenidoDeLaGuia guia={guia.data} />
          )}
        </div>

        {/* El pie son DOS filas y no una: el estado arriba, a lo ancho, y los
            botones abajo en una sola línea. Compartiendo fila, el texto se
            comía la mitad del ancho y los botones caían en dos renglones
            desalineados. Descargar va a la izquierda y aparte: no cambia nada,
            y no debe leerse como una variante de enviar. */}
        <div className="space-y-3 border-t pt-4">
          <p
            className={
              terreno.disponible
                ? "text-success-soft-foreground text-sm font-semibold"
                : "text-warning-soft-foreground text-sm font-semibold"
            }
          >
            {terreno.disponible
              ? `Disponible en la app${terreno.habilitado_para?.nombre ? ` de ${terreno.habilitado_para.nombre}` : ""}${
                  terreno.habilitado_en ? ` desde el ${formatearMomento(terreno.habilitado_en)}` : ""
                }.`
              : bloqueado ??
                "Aún no disponible: el informe no aparecerá en la app hasta que lo envíes."}
          </p>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="outline" onClick={descargar} disabled={descargando}>
              {descargando ? <Spinner /> : <DownloadIcon className="size-4" />}
              Descargar guía en PDF
            </Button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {terreno.puede_gestionar && terreno.disponible ? (
                <Button type="button" variant="outline" onClick={retirarDeLaApp} disabled={enVuelo}>
                  Retirar de la app
                </Button>
              ) : null}

              {terreno.puede_gestionar ? (
                <Button
                  type="button"
                  onClick={enviarAlEncargado}
                  disabled={enVuelo || bloqueado !== null}
                >
                  {enviar.isPending && !enviar.isPaused ? <Spinner /> : null}
                  {terreno.disponible ? "Reenviar guía" : "Enviar al encargado de terreno"}
                </Button>
              ) : null}
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Encabezado({ guia }: { guia: GuiaDeTerreno }) {
  const { propiedad, cliente, numero_informe, encargado } = guia.encabezado;

  return (
    <>
      {[propiedad, cliente, numero_informe ? `Informe ${numero_informe}` : null]
        .filter(Boolean)
        .join(" · ")}
      <br />
      Encargado en terreno: {encargado ?? "sin asignar"}
    </>
  );
}

function ContenidoDeLaGuia({ guia }: { guia: GuiaDeTerreno }) {
  return (
    <div className="space-y-4 pb-2">
      {guia.pendientes.length > 0 ? (
        <div className="bg-warning-soft rounded-lg p-4">
          <p className="text-warning-soft-foreground text-label mb-2">
            Pendientes del expediente ({guia.pendientes.length})
          </p>
          <ul className="text-ink-body list-disc space-y-1 pl-5 text-sm">
            {guia.pendientes.map((pendiente) => (
              <li key={pendiente.texto}>{pendiente.texto}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {guia.secciones.map((seccion) => {
        // Una sección sin filas ni nada que pedir (Otros antecedentes) no tiene
        // «0 registros»: es opcional, igual que en el PDF.
        const opcional = seccion.estado === "con_datos" && seccion.cantidad === 0;
        const estado = opcional
          ? { variante: "outline" as const, rotulo: () => "Opcional" }
          : ESTADO_DE_SECCION[seccion.estado];

        return (
          <section key={seccion.clave} className="rounded-lg border p-4">
            <div className="mb-2 flex items-start justify-between gap-3">
              <h3 className="text-h3 text-ink">{seccion.titulo}</h3>
              <Badge variant={estado.variante}>{estado.rotulo(seccion.cantidad)}</Badge>
            </div>

            {seccion.filas.length > 0 ? (
              <Table>
                <TableBody>
                  {seccion.filas.map((fila, indice) => (
                    <TableRow key={`${fila.nombre}-${indice}`}>
                      <TableCell className="text-ink w-2/5 font-semibold whitespace-normal">
                        {fila.nombre}
                      </TableCell>
                      <TableCell className="text-ink-body whitespace-normal">{fila.detalle}</TableCell>
                      <TableCell className="text-info-soft-foreground w-32 text-right text-xs">
                        {fila.pide_foto ? (
                          <span className="inline-flex items-center gap-1">
                            <CameraIcon className="size-3.5" />
                            Fotografiar
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : seccion.sin_datos_texto ? (
              <p className="text-ink-muted text-sm italic">{seccion.sin_datos_texto}</p>
            ) : null}

            <p className="text-ink-body mt-3 text-sm">
              <span className="font-semibold">Qué validar:</span> {seccion.instruccion}
            </p>
            <p className="text-info-soft-foreground mt-1 text-sm">
              <span className="font-semibold">Preguntar en terreno:</span> {seccion.pregunta}
            </p>
          </section>
        );
      })}
    </div>
  );
}
